import { useState, useEffect, useRef } from "react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  getDay,
  getDaysInMonth,
  isBefore,
  isAfter
} from "date-fns";

import rightArrow from "assets/images/dropdown/rightArrow.svg";
import leftArrow from "assets/images/dropdown/leftArrow.svg";
import calender from "assets/images/dropdown/calender.svg";
import calenderDark from "assets/images/dropdown/calenderDark.svg";
import "./datePicker.scss";
import Button from "components/Button/index";
import useTheme from "hooks/observer";

const DatePicker = ({ topPosition, setSelectedDate, selectedDate }) => {
  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today

  const monthYear = format(currentDate, "MMMM yyyy");
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getDay(startOfMonth(currentDate));
  const prevMonthDays = getDaysInMonth(subMonths(currentDate, 1));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper to check if currentDate is same or after today's month/year
  const isCurrentMonthOrAfter = () => {
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();

    return (
      currentYear > todayYear ||
      (currentYear === todayYear && currentMonth >= todayMonth)
    );
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    clickedDate.setHours(0, 0, 0, 0); // Normalize clicked date

    // Prevent selecting future dates
    if (isAfter(clickedDate, today)) return;

    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(clickedDate);
      setRangeEnd(null);
    } else if (isBefore(clickedDate, rangeStart)) {
      setRangeStart(clickedDate);
      setRangeEnd(rangeStart);
    } else {
      setRangeEnd(clickedDate);
    }
  };

  const isInRange = (day) => {
    const currentDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    currentDay.setHours(0, 0, 0, 0);

    return (
      rangeStart &&
      rangeEnd &&
      isAfter(currentDay, rangeStart) &&
      isBefore(currentDay, rangeEnd)
    );
  };

  const isStart = (day) => {
    return (
      rangeStart &&
      day === rangeStart.getDate() &&
      currentDate.getMonth() === rangeStart.getMonth() &&
      currentDate.getFullYear() === rangeStart.getFullYear()
    );
  };

  const isEnd = (day) => {
    return (
      rangeEnd &&
      day === rangeEnd.getDate() &&
      currentDate.getMonth() === rangeEnd.getMonth() &&
      currentDate.getFullYear() === rangeEnd.getFullYear()
    );
  };
  const handleSelectDates = () => {
    if (rangeStart && rangeEnd) {
      setSelectedDate(
        `${format(rangeStart, "d/M/yyyy")} - ${format(rangeEnd, "d/M/yyyy")}`
      );
      setShowPicker(false);
    }
  };

  return (
    <div className="datePicker" ref={pickerRef}>
      <div
        className="inputBox"
        onClick={() => setShowPicker(!showPicker)}
        role="button"
        tabIndex={0}
      >
        <input
          type="text"
          placeholder="Select Date Range"
          value={selectedDate || ""}
          readOnly
        />
        <span className="calendarIcon">
          <img
            src={theme === "light" ? calender : calenderDark}
            alt="calendar"
          />
        </span>
      </div>

      {showPicker && (
        <div className="calendar" style={{ top: topPosition ? "-370px" : "" }}>
          <div className="calendarHeader">
            <span
              className="arrow"
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            >
              <img src={leftArrow} alt="Previous" />
            </span>
            <span className="monthYear">{monthYear}</span>
            <span
              className={`arrow ${isCurrentMonthOrAfter() ? "disabled" : ""}`}
              onClick={() => {
                if (!isCurrentMonthOrAfter())
                  setCurrentDate(addMonths(currentDate, 1));
              }}
            >
              <img src={rightArrow} alt="Next" />
            </span>
          </div>

          <div className="daysGrid">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day} className="dayLabel">
                {day}
              </div>
            ))}

            {[...Array(firstDay)].map((_, index) => (
              <div key={`empty-${index}`} className="day disabled">
                {prevMonthDays - firstDay + index + 1}
              </div>
            ))}

            {[...Array(daysInMonth)].map((_, index) => {
              const day = index + 1;
              const date = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                day
              );
              date.setHours(0, 0, 0, 0); // Normalize date

              // Disable future dates
              const isDisabled = isAfter(date, today);

              const isToday =
                format(date, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");

              return (
                <div
                  key={day}
                  className={`day ${isDisabled ? "disabled" : ""}
                    ${isInRange(day) ? "inRange" : ""}
                    ${isStart(day) ? "selectedStart" : ""}
                    ${isEnd(day) ? "selectedEnd" : ""}
                    ${isToday ? "todayHighlight" : ""}`}
                  onClick={() => !isDisabled && handleDateClick(day)}
                  role="button"
                  tabIndex={0}
                >
                  {day}
                </div>
              );
            })}
          </div>

          <div className="calendarFooter">
            <div className="cancelWrapper">
              <div
                className="cancelButton"
                onClick={() => setShowPicker(false)}
              >
                <p>Cancel</p>
              </div>
            </div>
            <Button
              type="violet"
              title="Select"
              onClick={handleSelectDates}
              disabled={!rangeStart || !rangeEnd}
              width="fit-content"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
